import { useMemo } from 'react';
import {
  ReproveConfig,
  AnswerMap,
  ReproveScoring,
  TabScore,
  AnswerValue,
} from '../types/reprove';

export function useReproveScoring(
  config: ReproveConfig,
  answers: AnswerMap
): ReproveScoring {
  return useMemo(() => {
    if (!config || !config.tabs || config.tabs.length === 0) {
      return {
        tabScores: [],
        totalPct: 0,
        totalNaCount: 0,
      };
    }

    let totalNaCount = 0;

    // Calcular puntuaciones por tab con ajuste dinámico por N/A
    const tabScores: TabScore[] = config.tabs.map((tab) => {
      const tabAnswers = answers[tab.index] ?? {};
      let earnedPoints = 0;
      let maxPoints = 0;
      let naCount = 0;
      let naPoints = 0;

      if (!tab.questions || tab.questions.length === 0) {
        return {
          tabIndex: tab.index,
          title: tab.title,
          weightPct: tab.weightPct,
          earnedPct: 0,
          maxPct: tab.weightPct,
          naCount: 0,
          adjustedWeightPct: tab.weightPct,
        };
      }

      // Calcular puntos por pregunta
      tab.questions.forEach((q) => {
        const questionPoints = q.points || 1;

        maxPoints += questionPoints;
        const answerValue = tabAnswers[q.id] as AnswerValue;

        if (answerValue === true) {
          earnedPoints += questionPoints;
        } else if (answerValue === 'na') {
          naCount++;
          naPoints += questionPoints;
          totalNaCount++;
        }
      });

      // Restar los puntos N/A del máximo
      const adjustedMaxPoints = maxPoints - naPoints;
      let earnedPct = 0;
      let adjustedWeightPct = tab.weightPct;

      if (adjustedMaxPoints > 0) {
        // Calcular porcentaje de éxito
        const successRate = earnedPoints / adjustedMaxPoints;

        // Ajustar el peso del tab
        if (naPoints > 0 && maxPoints > 0) {
          adjustedWeightPct = tab.weightPct * (adjustedMaxPoints / maxPoints);
        }

        // Calcular puntos ganados
        earnedPct = successRate * adjustedWeightPct;
      } else if (naCount === tab.questions.length) {
        // Todo N/A = 0
        adjustedWeightPct = 0;
        earnedPct = 0;
      }

      return {
        tabIndex: tab.index,
        title: tab.title,
        weightPct: tab.weightPct,
        earnedPct,
        maxPct: adjustedWeightPct,
        naCount,
        adjustedWeightPct,
      };
    });

    // Calcular el peso total ajustado
    const adjustedTotalWeight = tabScores.reduce((acc, s) => acc + s.maxPct, 0);
    const totalEarned = tabScores.reduce((acc, s) => acc + s.earnedPct, 0);

    // Normalizar sobre 100%
    let totalPct =
      adjustedTotalWeight > 0
        ? Math.min(100, (totalEarned / adjustedTotalWeight) * 100)
        : 0;

    if (isNaN(totalPct) || !isFinite(totalPct)) {
      totalPct = 0;
    }
    totalPct = Math.max(0, Math.min(100, totalPct));
    return {
      tabScores,
      totalPct,
      totalMaxPct: 100,
      totalNaCount,
    };
  }, [config, answers]);
}

/**
 *  Hook adicional con mejor logging para debugging
 */
export function useReproveStats(
  config: ReproveConfig,
  answers: AnswerMap
): {
  totalQuestions: number;
  answeredQuestions: number;
  completionPct: number;
  tabsCompleted: number;
  unansweredTabs: string[];
  naQuestions: number;
} {
  return useMemo(() => {
    if (!config || !config.tabs) {
      return {
        totalQuestions: 0,
        answeredQuestions: 0,
        completionPct: 0,
        tabsCompleted: 0,
        unansweredTabs: [],
        naQuestions: 0,
      };
    }

    let totalQuestions = 0;
    let answeredQuestions = 0;
    let naQuestions = 0;
    let tabsCompleted = 0;
    const unansweredTabs: string[] = [];

    config.tabs.forEach((tab) => {
      const tabAnswers = answers[tab.index] ?? {};
      const tabQuestionCount = tab.questions.length;
      totalQuestions += tabQuestionCount;

      let tabAnswerCount = 0;
      tab.questions.forEach((q) => {
        const answer = tabAnswers[q.id] as AnswerValue;
        if (answer === true || answer === false || answer === 'na') {
          answeredQuestions++;
          tabAnswerCount++;
          if (answer === 'na') {
            naQuestions++;
          }
        }
      });

      if (tabAnswerCount === tabQuestionCount && tabQuestionCount > 0) {
        tabsCompleted++;
      } else if (tabQuestionCount > 0) {
        unansweredTabs.push(tab.title);
      }
    });

    const completionPct =
      totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

    return {
      totalQuestions,
      answeredQuestions,
      completionPct,
      tabsCompleted,
      unansweredTabs,
      naQuestions,
    };
  }, [config, answers]);
}

/**
 * Hook para validar respuestas y detectar inconsistencias
 */
export function useReproveValidation(
  config: ReproveConfig,
  answers: AnswerMap
): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} {
  return useMemo(() => {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!config || !config.tabs) {
      errors.push('Configuración no válida');
      return { isValid: false, errors, warnings };
    }

    // Validar cada tab
    config.tabs.forEach((tab) => {
      const tabAnswers = answers[tab.index] ?? {};

      // Verificar que todas las preguntas tienen respuesta
      tab.questions.forEach((question) => {
        const answer = tabAnswers[question.id] as AnswerValue;
        if (answer !== true && answer !== false && answer !== 'na') {
          warnings.push(
            `Pregunta sin responder en ${tab.title}: "${question.text}"`
          );
        }
      });

      // Verificar respuestas huérfanas
      Object.keys(tabAnswers).forEach((questionId) => {
        const questionExists = tab.questions.some((q) => q.id === questionId);
        if (!questionExists) {
          warnings.push(
            `Respuesta huérfana encontrada en ${tab.title}: ${questionId}`
          );
        }
      });
    });

    // Verificar tabs huérfanos en answers
    Object.keys(answers).forEach((tabIndexStr) => {
      const tabIndex = parseInt(tabIndexStr);
      const tabExists = config.tabs.some((t) => t.index === tabIndex);
      if (!tabExists) {
        warnings.push(`Respuestas para tab inexistente: ${tabIndex}`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }, [config, answers]);
}
